import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ProductTile from "@/components/store/ProductTile";
import {
  Package,
  Sparkles,
  ArrowUpRight,
  Coffee,
  Utensils,
  ShoppingBag,
  ShieldCheck,
} from "lucide-react";

/**
 * /store/bundles — 3 curated solution kits (Coffee, Restaurant, Boutique).
 * Each bundle detail lets the visitor bump quantities and see a live
 * total. "Save as project" hands off to the BOM save flow (POST
 * /store/bom/save) — reuses the same backend surface.
 */

const BUNDLE_ICON = {
  "coffee-shop-kit": Coffee,
  "restaurant-kit": Utensils,
  "boutique-kit": ShoppingBag,
};

export function StoreBundles() {
  const [bundles, setBundles] = useState([]);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/store/bundles");
        setBundles(r.data.items || []);
      } catch {
        setError("Couldn't load bundles.");
      }
    })();
  }, []);

  return (
    <PublicShell>
      <div data-testid="store-bundles" className="max-w-6xl mx-auto px-6 pt-32 pb-24">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
            <Sparkles size={12} /> Store · Solution Bundles
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white">
            Ready-to-deploy kits.
            <br />
            <span className="text-[#D4B878]">Pick one. Roll it out.</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Every kit is a hand-picked BOM curated from the Mirrorloops
            catalogue — tuned tier, right-sized quantities, editable line
            items. Save as a project or request quotes on any single line.
          </p>
        </div>

        {error && <div className="text-red-400 text-sm mb-6">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {bundles.map((b) => {
            const Icon = BUNDLE_ICON[b.slug] || Package;
            return (
              <Link
                key={b.slug}
                to={`/store/bundles/${b.slug}`}
                data-testid={`bundle-card-${b.slug}`}
                className="group border border-white/10 bg-white/[0.02] rounded-lg p-6 hover:border-[#D4B878]/50 transition-all"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{
                    backgroundColor: `${b.hero_accent}20`,
                    color: b.hero_accent,
                  }}
                >
                  <Icon size={26} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-1">
                  {b.industry}
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">{b.name}</h2>
                <p className="text-sm text-white/60 leading-relaxed mb-4 line-clamp-3">
                  {b.tagline}
                </p>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-white/50">{b.picks.length} products</span>
                  <span className="font-mono text-[#E8D6A3]">
                    ~${b.grand_total_estimate_usd.toLocaleString()}
                  </span>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-1 text-[11px] text-[#D4B878]">
                  See kit <ArrowUpRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </PublicShell>
  );
}

export function StoreBundleDetail() {
  const { slug } = useParams();
  const [bundle, setBundle] = useState(null);
  const [items, setItems] = useState({}); // { product_id: quantity_edits }
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/store/bundles/${slug}`);
        setBundle(r.data);
        const initial = {};
        r.data.picks.forEach((p) => {
          initial[p.product.id] = p.quantity;
        });
        setItems(initial);
      } catch (e) {
        setError(e?.response?.status === 404 ? "Bundle not found." : "Couldn't load bundle.");
      }
    })();
  }, [slug]);

  const total = useMemo(() => {
    if (!bundle) return 0;
    const TIER_COST = { Budget: 220, Mainstream: 550, Professional: 1550, Enterprise: 4200 };
    return bundle.picks.reduce((acc, p) => {
      const tier = p.product.commercial_tier || "Mainstream";
      const q = items[p.product.id] ?? p.quantity;
      return acc + (TIER_COST[tier] || 550) * q;
    }, 0);
  }, [bundle, items]);

  const grouped = useMemo(() => {
    if (!bundle) return [];
    // Use bundle.sections which is server-computed grouping
    return bundle.sections || [];
  }, [bundle]);

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/60">
          <div className="text-lg font-semibold text-red-300 mb-4">{error}</div>
          <Link to="/store/bundles" className="text-[#D4B878] underline">
            ← Back to Bundles
          </Link>
        </div>
      </PublicShell>
    );
  }
  if (!bundle) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/40">Loading…</div>
      </PublicShell>
    );
  }

  const Icon = BUNDLE_ICON[bundle.slug] || Package;

  return (
    <PublicShell>
      <div
        data-testid={`bundle-detail-${bundle.slug}`}
        className="max-w-5xl mx-auto px-6 pt-32 pb-24"
      >
        <div className="text-[11px] text-white/40 mb-4 flex items-center gap-2 font-mono">
          <Link to="/store" className="hover:text-white">Store</Link>
          <span>/</span>
          <Link to="/store/bundles" className="hover:text-white">Bundles</Link>
          <span>/</span>
          <span className="text-white/70">{bundle.slug}</span>
        </div>

        {/* Hero */}
        <div
          className="border rounded-lg p-6 mb-10"
          style={{
            borderColor: `${bundle.hero_accent}40`,
            backgroundImage: `linear-gradient(135deg, ${bundle.hero_accent}10, transparent 60%)`,
          }}
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center shrink-0"
              style={{
                backgroundColor: `${bundle.hero_accent}20`,
                color: bundle.hero_accent,
              }}
            >
              <Icon size={30} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-1">
                {bundle.industry}
              </div>
              <h1 className="text-3xl font-semibold text-white leading-tight">
                {bundle.name}
              </h1>
              <p className="text-sm text-white/70 mt-2 max-w-2xl">{bundle.tagline}</p>
              <div
                className="mt-4 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border"
                data-testid="bundle-compat-badge"
                style={{ borderColor: `${bundle.hero_accent}50`, color: bundle.hero_accent }}
              >
                <ShieldCheck size={11} />
                {bundle.picks.length} product{bundle.picks.length === 1 ? "" : "s"} · Mirrorloops-aware
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">
                Rough estimate
              </div>
              <div
                className="text-3xl font-bold font-mono"
                data-testid="bundle-total"
                style={{ color: bundle.hero_accent }}
              >
                ${total.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Sectioned items */}
        {grouped.map((sec) => (
          <section
            key={sec.key}
            data-testid={`bundle-section-${sec.key}`}
            className="mb-8"
          >
            <h2 className="text-lg font-semibold text-white mb-3">{sec.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sec.items.map((it) => (
                <div
                  key={it.product.id}
                  data-testid={`bundle-item-${it.product.id}`}
                  className="border border-white/10 bg-white/[0.02] rounded-lg p-3 flex gap-3"
                >
                  <div className="w-24 h-24 shrink-0">
                    <ProductTile p={it.product} size="sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-[#D4B878] font-semibold mb-0.5 truncate">
                      {it.product.manufacturer}
                    </div>
                    <Link
                      to={`/store/product/${it.product.id}`}
                      className="text-sm font-medium text-white hover:text-[#E8D6A3] leading-tight block mb-2 line-clamp-2"
                    >
                      {it.product.name}
                    </Link>
                    <div className="flex items-center gap-2 text-[11px]">
                      <label className="text-white/40 uppercase tracking-[0.14em] text-[9px]">qty</label>
                      <input
                        type="number"
                        min="0"
                        max="9999"
                        value={items[it.product.id] ?? it.quantity}
                        onChange={(e) =>
                          setItems({ ...items, [it.product.id]: parseInt(e.target.value || "0", 10) })
                        }
                        data-testid={`bundle-qty-${it.product.id}`}
                        className="w-14 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <Button
            onClick={() => {
              const params = new URLSearchParams({
                store_type: bundle.industry.toLowerCase().split("/")[0],
              });
              nav(`/store/build?${params.toString()}`);
            }}
            data-testid="bundle-open-in-bom"
            className="rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold"
          >
            Customize in BOM Builder
          </Button>
          <Link
            to="/store/bundles"
            className="text-xs text-white/50 hover:text-white ml-2"
          >
            ← Back to all bundles
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}
